import { Fragment, useEffect, useMemo } from 'react';
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DataUnavailable } from '@components/DataUnavailable/DataUnavailable';
import { APP_CONTENT_PANE_ATTR } from '@hooks/useAppContentPane';
import type { IHelpGuideSummary } from '@apis/help';
import { useLang } from '@hooks/useLang';
import { useHelpGuide, useHelpGuides } from './useHelp';
import { buildHelpTree, siblingsOf } from './helpTree';
import { GuideIconTile } from './GuideIconTile';
import { formatUpdated } from './helpFormat';
import { GuideRail } from './GuideRail';
import { GuideToc } from './GuideToc';
import { GuideBody } from './GuideBody';
import { GuideFooter } from './GuideFooter';
import { clearMarks, flashElement, flashMarks, wrapMatch } from './findInGuide';
import { glideIntoView } from './glideIntoView';
import styles from './GuideReaderPage.module.css';

// A stable empty list: a fresh `[]` per render would defeat GuideBody's sanitize memo.
const NO_REPORTS: IHelpGuideSummary[] = [];

export function GuideReaderPage() {
  const { slug: routeSlug = '' } = useParams<{ slug: string }>();
  const { hash } = useLocation();
  const [params] = useSearchParams();
  const find = params.get('find');
  const occurrence = params.get('n');
  const { t, i18n } = useTranslation();
  const language = useLang();
  const { guides } = useHelpGuides();
  const { guide, isError, isSwitching } = useHelpGuide(routeSlug);
  // The guide on screen: while the next one loads, the previous one stays up.
  const slug = guide?.slug ?? routeSlug;
  const tree = useMemo(() => buildHelpTree(guides), [guides]);
  const family = siblingsOf(tree, slug);
  const ownNode = tree.find((node) => node.guide.slug === slug);
  const reports = ownNode ? ownNode.children : NO_REPORTS;
  const familySlug = family.parent ? family.parent.slug : reports.length > 0 ? slug : null;

  useEffect(() => {
    if (!guide || isSwitching) return;
    const heading = hash ? document.getElementById(hash.slice(1)) : null;
    // Arriving from a search result: glide to just the searched words and flash
    // them (the shared jump flash of the progress tracker and the delivery
    // board) as the glide closes in.
    const body = document.querySelector<HTMLElement>('[data-guide-body]')!; // rendered with `guide`
    const scope = occurrence !== null ? body : heading;
    const marks = find && scope ? wrapMatch(scope, find, Number(occurrence ?? 0)) : null;
    if (marks) {
      const cancel = glideIntoView(marks[0]!, () => flashMarks(marks, styles.matchFlash!));
      // Leaving mid-glide (another result, or StrictMode's re-run) must not strand the marks.
      return () => {
        cancel();
        clearMarks(marks);
      };
    }
    if (find && heading) {
      // The match is gone (the guide changed since the search): flash its section instead.
      return glideIntoView(heading, () => flashElement(heading, styles.matchFlash!));
    }
    // A section picked from the table of contents glides there too, rather than jumping.
    if (heading) return glideIntoView(heading, () => {}, 'start');
    (body.closest(`[${APP_CONTENT_PANE_ATTR}]`) ?? document.documentElement).scrollTop = 0;
  }, [guide, isSwitching, hash, find, occurrence]);

  if (isError) {
    return <DataUnavailable className={styles.notFound} message={t('help.notFound')} />;
  }

  const headings = guide ? (reports.length > 0 ? [...guide.headings, { id: 'reports', text: t('help.reportGuidesHeading'), level: 2 as const }] : guide.headings) : [];

  return (
    <div className={styles.reader}>
      <GuideRail tree={tree} slug={slug} familySlug={familySlug} />
      <article className={styles.article}>
        {guide ? (
          <>
            <header className={styles.head}>
              <GuideIconTile iconKey={guide.iconKey} variant="head" />
              <div>
                {family.parent ? (
                  <Link className={styles.eyebrowLink} to={`/help/${family.parent.slug}`}>
                    {t('help.reportOf', { parent: family.parent.title, n: family.index + 1, total: family.siblings.length })}
                  </Link>
                ) : null}
                <h1>{guide.title}</h1>
                <div className={styles.headMeta}>
                  {t('help.minRead', { count: guide.minutes })} · {t('help.updated', { date: formatUpdated(guide.updatedAt, i18n.language) })}
                </div>
              </div>
            </header>
            {!isSwitching && guide.language !== language ? <p className={styles.notTranslated} role="status">{t('help.notTranslated')}</p> : null}
            <Fragment key={`${slug}:${guide.language}`}>
              <GuideBody html={guide.html} reports={reports} />
              <GuideFooter family={family.parent ? { parent: family.parent, siblings: family.siblings, index: family.index } : null} />
            </Fragment>
          </>
        ) : null}
      </article>
      <GuideToc slug={slug} headings={headings} siblings={family.parent ? { parent: family.parent, list: family.siblings } : null} />
    </div>
  );
}
